// Shared react-table (v7) sort comparator used by the reusable <Table /> component.
//
// It handles the shapes that show up across Opspot's tables:
//   - plain strings  -> case-insensitive, natural-number-aware compare
//   - dates          -> compared chronologically (ISO strings or Date objects)
//   - numbers        -> compared numerically
//   - arrays         -> "multiple text results" sort by their first value
//   - empty values   -> always sorted to the bottom
//
// react-table only looks at the sign of the return value and flips it itself
// for descending sorts, so we never account for direction here.

// A value can be a "multiple text results" array (e.g. customer_names). Sort by
// the first entry in that case.
function firstValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

function isEmpty(value) {
  return value === null || value === undefined || value === "";
}

// Strictly numeric strings/numbers only — avoids treating "123 Main St" as 123.
function isNumeric(value) {
  if (typeof value === "number") return Number.isFinite(value);
  return typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value.trim());
}

// Parseable date that isn't just a bare number (so "42" stays a number).
function isDateLike(value) {
  if (value instanceof Date) return true;
  if (typeof value !== "string") return false;
  return /[-/:T]/.test(value) && !Number.isNaN(Date.parse(value));
}

function smartCompare(rowA, rowB, columnId) {
  const a = firstValue(rowA.values[columnId]);
  const b = firstValue(rowB.values[columnId]);

  const aEmpty = isEmpty(a);
  const bEmpty = isEmpty(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (isDateLike(a) && isDateLike(b)) {
    return new Date(a).getTime() - new Date(b).getTime();
  }

  if (isNumeric(a) && isNumeric(b)) {
    return Number(a) - Number(b);
  }

  return String(a).localeCompare(String(b), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

export { smartCompare };
